import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, API_BASE, formatApiErrorDetail } from "@/lib/api";
import { formatWIBDateTime24 } from "@/lib/time";
import { useStatusReference, themeForStatus } from "@/lib/statusTheme";
import { computeThisMonthRange, computeRollingDaysRange, emptyRange } from "@/lib/exportDates";
import { sortRecentTicketsDesc } from "@/lib/ticketSort";
import { useAuth } from "@/context/AuthContext";
import TicketForm from "@/components/TicketForm";
import RouteButtons from "@/components/RouteButtons";
import PullToRefresh from "@/components/PullToRefresh";
import { Plus, Search, Upload, Pencil, Trash2, Eye, Filter, Download, Hand, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const PRIORITY_COLORS = {
  P1: "#991B1B", P2: "#EA580C", P3: "#1E40AF", P4: "#4B5563",
};

const STATUS_LIST = ["OPEN", "SCHEDULED", "PROSES", "DONE", "RESCHEDULE", "WAITING", "CANCEL"];

// Feb 2026 — Pagination page-size options (Manajemen Pekerjaan). Mirrors
// Customer Billing control UX; frontend-only slice over loaded rows.
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function fmtIDR(n) {
  return new Intl.NumberFormat("id-ID").format(n || 0);
}

export default function Tickets() {
  const { user, hasPriv } = useAuth();
  const statusMaster = useStatusReference();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const canManage = user?.role === "admin" || user?.role === "koordinator";
  // Fase 5.10 — Manajemen Pekerjaan is a management surface. Teknisi is
  // redirected to /pool (their operational surface) if they land here via
  // deep-link / Dashboard drilldown / typed URL. This is defence-in-depth
  // in addition to hiding the sidebar entry in Layout.jsx.
  useEffect(() => {
    if (user?.role === "teknisi") {
      navigate("/pool", { replace: true });
    }
  }, [user?.role, navigate]);
  const canImport = hasPriv("excel_import");
  const canExport = hasPriv("excel_export");
  const canCreate = hasPriv("ticket_create");
  const canDelete = hasPriv("ticket_delete");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  // Feb 2026 — Pagination state (frontend slice; endpoint tetap return full set).
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [wilayahList, setWilayahList] = useState([]);
  // Feb 2026 — Filter Jenis Pekerjaan. Options dari `/api/reference`
  // (master existing yang juga digunakan form Tiket Baru).
  const [jenisList, setJenisList] = useState([]);
  const [filters, setFilters] = useState(() => {
    // Default DARI = 1st of running month (WIB local), SAMPAI = today.
    // URL query params from Dashboard drill-down still take priority so a
    // deep-link to a specific ticket window keeps working.
    const monthDefault = computeThisMonthRange();
    let status = "", wilayah = "", jenis_pekerjaan = "", pic = "", date_from = monthDefault.date_from, date_to = monthDefault.date_to;
    if (typeof window !== "undefined") {
      const usp = new URLSearchParams(window.location.search);
      status = usp.get("status") || "";
      wilayah = usp.get("wilayah") || "";
      jenis_pekerjaan = usp.get("jenis_pekerjaan") || "";
      pic = usp.get("pic") || "";
      if (usp.get("date_from")) date_from = usp.get("date_from");
      if (usp.get("date_to")) date_to = usp.get("date_to");
    }
    return { q: "", status, prioritas: "", wilayah, pic, jenis_pekerjaan, date_from, date_to };
  });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [readOnly, setReadOnly] = useState(false);
  const [initialTab, setInitialTab] = useState("detail");
  const [highlight, setHighlight] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selected, setSelected] = useState(new Set());   // ticket ids selected for bulk delete
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportRange, setExportRange] = useState(() => computeThisMonthRange());

  // Feb 2026 — Sinkronkan dialog Ekspor dengan filter tanggal aktif.
  //   Sebelumnya: setiap kali dialog dibuka, exportRange di-reset ke
  //   "Bulan Ini" — mengabaikan filter tanggal yang sedang aktif di
  //   Manajemen Pekerjaan. Sekarang: pakai `filters.date_from/date_to`
  //   bila salah satunya truthy (yang notabene juga default "Bulan Ini");
  //   fallback ke computeThisMonthRange bila keduanya kosong (mode
  //   Reset+Semua). Preset ("Bulan Ini/7/30/Semua") tetap bisa override.
  useEffect(() => {
    if (!exportOpen) return;
    if (filters.date_from || filters.date_to) {
      setExportRange({ date_from: filters.date_from || "", date_to: filters.date_to || "" });
    } else {
      setExportRange(computeThisMonthRange());
    }
  }, [exportOpen, filters.date_from, filters.date_to]);
  const fileRef = useRef(null);

  const load = async (override) => {
    // `override` lets callers (e.g. Reset) pass the freshly-computed filters
    // without waiting for React's async state flush — this avoids the
    // stale-closure bug where `setFilters(...); load()` re-runs with the
    // OLD filters because `load` is still bound to the previous render.
    const active = override || filters;
    setLoading(true);
    try {
      // Fase 5.8 — When a status filter is applied, DROP `date_field=tgl_masuk`
      // so the backend switches to occurrence-based semantics (a ticket that
      // ENTERED the requested status inside the period is matched via the
      // SSoT event field: completed_at for DONE, cancelled_at for CANCEL,
      // history[] for transitions, created_at for initial OPEN). Without a
      // status filter, the tgl_masuk view (Manajemen Pekerjaan "creation
      // date") is kept as the operational default.
      const params = {};
      if (!active.status) {
        params.date_field = "tgl_masuk";
      }
      Object.entries(active).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/tickets", { params });
      // Server default order is `created_at desc`; Manajemen Pekerjaan needs
      // tgl_masuk desc (with created_at fallback for legacy rows). Sorting
      // client-side keeps this consistent with the Dashboard "Tiket Terakhir"
      // list and doesn't require a server-side sort mode.
      setRows(sortRecentTicketsDesc(data));
      // Feb 2026 — Setiap reload (Terapkan filter / Reset / mount) kembali
      // ke halaman 1 agar user tidak terdampar di halaman yang bisa jadi
      // di luar jumlah hasil baru.
      setPage(1);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get("/tickets/wilayah-list").then(({ data }) => setWilayahList(data)).catch(() => {});
    // Feb 2026 — Load daftar jenis pekerjaan dari master existing.
    // Bentuk item: {code, label}. Fallback: array kosong (dropdown hanya
    // menampilkan "Semua Jenis Pekerjaan").
    api.get("/reference").then(({ data }) => {
      setJenisList(Array.isArray(data?.jenis_pekerjaan) ? data.jenis_pekerjaan : []);
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  // Support ?open=<ticket_id> deep-link (from Dashboard mobile ticket-card list
  // or NotificationBell click). Reacts to BOTH rows-load AND URL changes so
  // that clicking a notification while already on /tickets still opens the
  // right ticket (URL changes without triggering a fresh rows fetch).
  // If the ticket is not in the currently loaded rows (e.g. filtered out by
  // date range), fall back to `GET /api/tickets/{id}` so notification click
  // always opens the correct ticket regardless of current filter.
  // Removes the param from the URL to avoid re-opens on subsequent renders.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const usp = new URLSearchParams(window.location.search);
    const openId = usp.get("open");
    if (!openId) return;
    // Optional ?tab=photos|history|detail — notification click may request a
    // specific tab (e.g. photo notifs open on Foto Bukti).
    const requestedTab = usp.get("tab");
    const tab = (requestedTab === "photos" || requestedTab === "history" || requestedTab === "detail")
      ? requestedTab
      : "detail";
    // Optional ?highlight=status — status-change notifications briefly
    // flash the STATUS field so admin can see what changed at a glance.
    const requestedHighlight = usp.get("highlight");
    const hl = requestedHighlight === "status" ? "status" : null;
    const stripParam = () => {
      usp.delete("open");
      usp.delete("tab");
      usp.delete("highlight");
      const newQs = usp.toString();
      window.history.replaceState({}, "", `${window.location.pathname}${newQs ? "?" + newQs : ""}`);
    };
    const match = rows.find((r) => r.id === openId);
    if (match) {
      setEditing(match);
      setReadOnly(false);
      setInitialTab(tab);
      setHighlight(hl);
      setOpen(true);
      stripParam();
      return;
    }
    // Not in current page → fetch directly. Silently no-op on 403/404
    // (permission denied or deleted ticket) — bell will still show the notif
    // for audit purposes but we don't spam an error toast.
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/tickets/${encodeURIComponent(openId)}`);
        if (cancelled || !data) return;
        setEditing(data);
        setReadOnly(false);
        setInitialTab(tab);
        setHighlight(hl);
        setOpen(true);
      } catch (_) { /* silent */ }
      // Strip only when this run finished; a cancelled run (rows reloaded)
      // must keep ?open so the re-run can still open the detail.
      finally { if (!cancelled) stripParam(); }
    })();
    return () => { cancelled = true; };
  }, [rows, location.search]);

  const askDelete = (row) => setDeleteTarget(row);

  const toggleSelect = (id) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleSelectAll = () => setSelected((s) =>
    s.size === rows.length ? new Set() : new Set(rows.map((r) => r.id))
  );
  const clearSelected = () => setSelected(new Set());

  const confirmBulkDelete = async () => {
    if (selected.size === 0) return;
    setDeleting(true);
    try {
      const { data } = await api.post("/tickets/bulk-delete", { ids: Array.from(selected) });
      toast.success(`${data.deleted} tiket dihapus`);
      clearSelected();
      setBulkConfirm(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal bulk delete");
    } finally {
      setDeleting(false);
    }
  };

  const downloadMonthlyPdf = async () => {
    const src = exportRange.date_from || exportRange.date_to || new Date().toISOString().slice(0, 10);
    const [y, m] = src.split("-");
    if (!y || !m) { toast.error("Isi kolom 'Dari' atau 'Sampai' untuk menentukan bulan"); return; }
    try {
      const params = new URLSearchParams({ year: y, month: String(parseInt(m, 10)) });
      // Feb 2026 — Sinkron dengan filter aktif Manajemen Pekerjaan supaya
      // rekap bulanan PDF terpotong sesuai jenis/prioritas/pic/q/wilayah.
      // Backend `/reports/monthly-recap.pdf` menerima additive optional
      // params. Status tidak diforward karena rekap sudah fixed DONE.
      if (filters.wilayah) params.set("wilayah", filters.wilayah);
      if (filters.jenis_pekerjaan) params.set("jenis_pekerjaan", filters.jenis_pekerjaan);
      if (filters.prioritas) params.set("prioritas", filters.prioritas);
      if (filters.pic) params.set("pic", filters.pic);
      if (filters.q) params.set("q", filters.q);
      const token = localStorage.getItem("aroma_token");
      const res = await fetch(`${API_BASE}/reports/monthly-recap.pdf?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`Gagal (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `rekap_done_${y}_${m.padStart(2, "0")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF rekap diunduh");
      setExportOpen(false);
    } catch (e) {
      toast.error(e.message || "Gagal unduh PDF");
    }
  };

  const onImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/tickets/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Berhasil import ${data.inserted} tiket`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      e.target.value = "";
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/tickets/${deleteTarget.id}`);
      toast.success(`Tiket ${deleteTarget.no_tiket || ""} berhasil dihapus`);
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal menghapus tiket");
    } finally {
      setDeleting(false);
    }
  };

  const onExport = async (range) => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      if (range?.date_from) params.set("date_from", range.date_from);
      if (range?.date_to)   params.set("date_to",   range.date_to);
      const token = localStorage.getItem("aroma_token");
      const res = await fetch(`${API_BASE}/reports/tickets-export?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error("Ekspor gagal");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const rangeSuffix = range?.date_from || range?.date_to
        ? `_${range.date_from || "awal"}_sd_${range.date_to || "sekarang"}`
        : "";
      a.download = `jadwal_harian${rangeSuffix}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("File Excel diunduh");
      setExportOpen(false);
    } catch (e) {
      toast.error(e.message || "Ekspor gagal");
    }
  };

  const inputCls =
    "h-10 px-3 rounded-md border border-[#E5E7EB] bg-white focus:outline-none focus:ring-2 focus:ring-[#0055FF] focus:border-transparent text-sm";

  // Feb 2026 — Frontend pagination slice. `rows` is the full result set
  // returned by `/tickets` (match filter active); `pageRows` adalah
  // potongan sesuai `page`/`pageSize` yang di-render di tabel desktop +
  // list mobile. Bulk-delete tetap operates terhadap `rows` (semua yang
  // ter-filter), identik perilaku existing.
  const pageRows = rows.slice((page - 1) * pageSize, page * pageSize);

  // Fase 5.10 guard — prevent teknisi from seeing management UI even for a
  // frame while useEffect above triggers the redirect.
  if (user?.role === "teknisi") return null;

  return (
    <PullToRefresh onRefresh={load}>
    <div className="space-y-6" data-testid="tickets-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#9CA3AF]">Operasional</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-1">Manajemen Pekerjaan</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canExport && (
            <button
              onClick={() => setExportOpen(true)}
              data-testid="btn-export"
              className="h-10 px-4 rounded-md border border-[#E5E7EB] bg-white hover:bg-[#F8F9FA] text-sm font-semibold flex items-center gap-2 transition-colors"
            >
              <Download className="h-4 w-4" /> Ekspor Excel
            </button>
          )}
          {canImport && (
            <>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" hidden onChange={onImport} data-testid="btn-import-file" />
              <button
                onClick={() => fileRef.current?.click()}
                data-testid="btn-import"
                className="h-10 px-4 rounded-md border border-[#E5E7EB] bg-white hover:bg-[#F8F9FA] text-sm font-semibold flex items-center gap-2 transition-colors"
              >
                <Upload className="h-4 w-4" /> Import Excel
              </button>
            </>
          )}
          {canCreate && (
            <button
              onClick={() => { setEditing(null); setReadOnly(false); setInitialTab("detail"); setHighlight(null); setOpen(true); }}
              data-testid="btn-new-ticket"
              className="h-10 px-4 rounded-md bg-[#0055FF] hover:bg-[#0044CC] text-white text-sm font-semibold flex items-center gap-2 transition-colors"
            >
              <Plus className="h-4 w-4" /> Tiket Baru
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E5E7EB] rounded-md p-4">
        <div className="flex items-center gap-2 mb-3 text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">
          <Filter className="h-3 w-3" /> Filter
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3">
          <div className="relative lg:col-span-2">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <input
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              placeholder="Cari no. tiket, lokasi, keterangan..."
              className={`${inputCls} w-full pl-10`}
              data-testid="filter-q"
            />
          </div>
          <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} className={inputCls} data-testid="filter-status">
            <option value="">Semua Status</option>
            {STATUS_LIST.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filters.prioritas} onChange={(e) => setFilters((f) => ({ ...f, prioritas: e.target.value }))} className={inputCls} data-testid="filter-prioritas">
            <option value="">Semua Prioritas</option>
            {["P1", "P2", "P3", "P4"].map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select
            value={filters.jenis_pekerjaan}
            onChange={(e) => setFilters((f) => ({ ...f, jenis_pekerjaan: e.target.value }))}
            className={inputCls}
            data-testid="filter-jenis-pekerjaan"
          >
            <option value="">Semua Jenis</option>
            {jenisList.map((j) => (
              <option key={j.code} value={j.code}>
                {j.label ? `${j.code} — ${j.label}` : j.code}
              </option>
            ))}
          </select>
          <input value={filters.wilayah} onChange={(e) => setFilters((f) => ({ ...f, wilayah: e.target.value }))} placeholder="Wilayah" className={inputCls} data-testid="filter-wilayah" list="filter-wilayah-list" />
          <datalist id="filter-wilayah-list">
            {wilayahList.map((w) => <option key={w} value={w}>{w}</option>)}
          </datalist>
          {/* Date pair: kept together as ONE grid item on tablet (sm:col-span-2)
              and desktop (lg:col-span-2) so DARI + SAMPAI never wrap into
              different rows. On mobile they stack vertically inside the same
              group so the pairing still reads as one filter. */}
          <div
            className="sm:col-span-2 lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3"
            data-testid="filter-date-group"
          >
            <div className="relative">
              <input type="date" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} className={`${inputCls} w-full`} data-testid="filter-date-from" placeholder="Dari" />
              <span className="absolute -top-2 left-2 bg-white px-1 text-[9px] font-bold uppercase tracking-widest text-[#9CA3AF]">Dari</span>
            </div>
            <div className="relative">
              <input type="date" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} className={`${inputCls} w-full`} data-testid="filter-date-to" placeholder="Sampai" />
              <span className="absolute -top-2 left-2 bg-white px-1 text-[9px] font-bold uppercase tracking-widest text-[#9CA3AF]">Sampai</span>
            </div>
          </div>
        </div>
        <div className="mt-3 flex gap-2 justify-end">
          <button
            onClick={() => {
              const monthDefault = computeThisMonthRange();
              const reset = {
                q: "", status: "", prioritas: "", wilayah: "", pic: "", jenis_pekerjaan: "",
                date_from: monthDefault.date_from, date_to: monthDefault.date_to,
              };
              setFilters(reset);
              load(reset);
            }}
            className="h-9 px-3 rounded-md border border-[#E5E7EB] text-sm hover:bg-[#F8F9FA] transition-colors"
            data-testid="btn-reset-filter"
          >
            Reset
          </button>
          <button
            onClick={() => load()}
            className="h-9 px-4 rounded-md bg-[#0A0A0A] hover:bg-[#333] text-white text-sm font-semibold transition-colors"
            data-testid="btn-apply-filter"
          >
            Terapkan
          </button>
        </div>
      </div>

      {/* Active-filter pills for filters not represented by a visible input
          (currently: jenis_pekerjaan, driven by Dashboard drill-down). Shows
          a clear × so users can drop the filter without hitting Reset. */}
      {filters.jenis_pekerjaan && (
        <div
          className="flex items-center flex-wrap gap-2 -mt-2"
          data-testid="active-filter-bar"
        >
          <span
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-[#EEF2FF] text-[#3730A3] border border-[#C7D2FE]"
            data-testid="active-filter-jenis"
          >
            <span className="text-[10px] uppercase tracking-widest text-[#6366F1]">Jenis</span>
            <span>{filters.jenis_pekerjaan}</span>
            <button
              type="button"
              onClick={() => {
                const next = { ...filters, jenis_pekerjaan: "" };
                setFilters(next);
                // Strip jenis_pekerjaan from URL so a reload doesn't
                // re-apply the drilldown filter (previously the chip
                // would come back on refresh).
                if (typeof window !== "undefined") {
                  const usp = new URLSearchParams(window.location.search);
                  usp.delete("jenis_pekerjaan");
                  const qs = usp.toString();
                  navigate(`/tickets${qs ? `?${qs}` : ""}`, { replace: true });
                }
                load(next);
              }}
              className="ml-0.5 -mr-1 h-5 w-5 inline-flex items-center justify-center rounded-full hover:bg-[#C7D2FE] text-[#3730A3]"
              aria-label="Hapus filter jenis pekerjaan"
              data-testid="active-filter-jenis-clear"
            >
              ×
            </button>
          </span>
        </div>
      )}

      <div className="bg-white border border-[#E5E7EB] rounded-md overflow-hidden">
        {/* Feb 2026 — Pagination control (Manajemen Pekerjaan). Mirror UX
            Customer Billing: posisi di ATAS tabel, selektor 10/25/50/100 +
            tombol prev/next. Reset ke halaman 1 saat page size berubah. */}
        <div className="flex items-center justify-between text-sm px-4 py-2 flex-wrap gap-2 border-b border-[#E5E7EB] bg-[#FAFAFA]" data-testid="tickets-pagination">
          <div className="text-[#4B5563]">
            {rows.length > pageSize
              ? <>Halaman {page} dari {Math.max(1, Math.ceil(rows.length / pageSize))} · Total {rows.length} tiket</>
              : <>Total {rows.length} tiket</>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="inline-flex items-center gap-1 text-xs text-[#6B7280]">
              Tampilkan
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                className="border rounded-md px-2 py-1 text-xs bg-white"
                data-testid="tickets-page-size"
              >
                {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              data
            </label>
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="h-7 px-2 rounded-md border border-[#E5E7EB] text-xs bg-white hover:bg-[#F8F9FA] disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="tickets-page-prev"
            >
              Sebelumnya
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={page * pageSize >= rows.length}
              className="h-7 px-2 rounded-md border border-[#E5E7EB] text-xs bg-white hover:bg-[#F8F9FA] disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="tickets-page-next"
            >
              Berikutnya
            </button>
          </div>
        </div>
        {canDelete && selected.size > 0 && (
          <div
            data-testid="bulk-toolbar"
            className="flex items-center justify-between gap-3 px-4 py-2 bg-[#FEF3C7] border-b border-[#FDE68A] text-sm"
          >
            <div className="text-[#92400E] font-semibold">
              {selected.size} tiket terpilih
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={clearSelected}
                className="h-8 px-3 rounded-md border border-[#FDE68A] bg-white text-xs font-semibold text-[#92400E] hover:bg-[#FEF3C7]"
              >
                Batalkan Pilihan
              </button>
              <button
                data-testid="btn-bulk-delete"
                onClick={() => setBulkConfirm(true)}
                className="h-8 px-3 rounded-md bg-[#991B1B] hover:bg-[#7F1D1D] text-white text-xs font-bold flex items-center gap-1.5"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Hapus {selected.size} Tiket
              </button>
            </div>
          </div>
        )}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] bg-[#F8F9FA]">
              <tr>
                {canDelete && (
                  <th className="px-3 py-3 w-8">
                    <input
                      type="checkbox"
                      data-testid="bulk-select-all"
                      checked={rows.length > 0 && selected.size === rows.length}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 rounded border-[#E5E7EB] cursor-pointer"
                    />
                  </th>
                )}
                <th className="px-3 py-3">Tanggal</th>
                <th className="px-3 py-3">No. Tiket</th>
                <th className="px-3 py-3">Jenis</th>
                <th className="px-3 py-3">Lokasi / Wilayah</th>
                <th className="px-3 py-3">PIC</th>
                <th className="px-3 py-3">Prioritas</th>
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3 text-right">Biaya</th>
                <th className="px-2 py-3 text-right sticky right-0 z-10 bg-[#F8F9FA] shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.08)]">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((r, i) => {
                const c = themeForStatus(r.status, statusMaster);
                return (
                  <motion.tr
                    key={r.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: Math.min(i * 0.01, 0.2) }}
                    className={`group border-t border-[#E5E7EB] hover:bg-[#F8F9FA] ${selected.has(r.id) ? "bg-[#FEF9C3]" : ""}`}
                    data-testid={`row-${r.id}`}
                  >
                    {canDelete && (
                      <td className="px-3 py-3 w-8">
                        <input
                          type="checkbox"
                          data-testid={`bulk-select-${r.id}`}
                          checked={selected.has(r.id)}
                          onChange={() => toggleSelect(r.id)}
                          className="h-4 w-4 rounded border-[#E5E7EB] cursor-pointer"
                        />
                      </td>
                    )}
                    <td className="px-3 py-3 text-xs text-[#4B5563] font-mono-tab">
                      <div className="whitespace-nowrap">{r.tgl_masuk || "-"}</div>
                      {r.completed_at && (
                        <div className="text-[#9CA3AF] whitespace-nowrap">
                          <span className="text-[#B0B6BE]">selesai:</span>{" "}
                          {formatWIBDateTime24(r.completed_at)}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 font-mono-tab font-semibold whitespace-nowrap">{r.no_tiket || "-"}</td>
                    <td className="px-3 py-3">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#F3F4F6] text-[#0A0A0A] border border-[#E5E7EB]">
                        {r.jenis_pekerjaan || "-"}
                      </span>
                    </td>
                    <td className="px-3 py-3 max-w-xs">
                      <div className="truncate">{r.objek_lokasi || "-"}</div>
                      <div className="text-xs text-[#9CA3AF]">{r.wilayah || "-"}</div>
                      {(r.lat || r.wilayah) && (
                        <div className="mt-1.5">
                          <RouteButtons ticket={r} size="xs" showLabel={false} />
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <div>{r.pic || "-"}</div>
                      {r.tim_pendamping && ((Array.isArray(r.tim_pendamping) && r.tim_pendamping.length > 0) || (typeof r.tim_pendamping === "string" && r.tim_pendamping.trim())) && (
                        <div className="text-xs text-[#9CA3AF]">
                          +{Array.isArray(r.tim_pendamping) ? r.tim_pendamping.join(", ") : r.tim_pendamping}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <span className="font-bold font-mono-tab" style={{ color: PRIORITY_COLORS[r.prioritas] || "#4B5563" }}>
                        {r.prioritas || "-"}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <span
                        className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border whitespace-nowrap inline-block"
                        style={{ background: c.bg, color: c.fg, borderColor: c.border }}
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right font-mono-tab whitespace-nowrap">Rp {fmtIDR(r.biaya)}</td>
                    <td className={`px-2 py-3 sticky right-0 z-10 shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.08)] ${selected.has(r.id) ? "bg-[#FEF9C3]" : "bg-white group-hover:bg-[#F8F9FA]"}`}>
                      <div className="flex items-center justify-end gap-1">
                        {["OPEN", "RESCHEDULE"].includes(r.status) && !(r.pic && r.pic !== user?.name) && (
                          <button
                            onClick={async () => {
                              try {
                                await api.post(`/tickets/${r.id}/claim`);
                                toast.success(`Tiket ${r.no_tiket} diklaim`);
                                load();
                              } catch (e) {
                                toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal klaim");
                              }
                            }}
                            className="h-8 px-2 rounded-md bg-[#0055FF] hover:bg-[#0044CC] text-white flex items-center gap-1 text-[11px] font-semibold"
                            data-testid={`btn-claim-${r.id}`}
                            title="Klaim tiket ini"
                          >
                            <Hand className="h-3.5 w-3.5" />
                            Klaim
                          </button>
                        )}
                        <button
                          onClick={() => { setEditing(r); setReadOnly(true); setInitialTab("detail"); setOpen(true); }}
                          className="h-8 w-8 rounded-md hover:bg-[#F3F4F6] flex items-center justify-center"
                          data-testid={`btn-view-${r.id}`}
                          title="Detail"
                        >
                          <Eye className="h-4 w-4 text-[#4B5563]" />
                        </button>
                        <button
                          onClick={() => { setEditing(r); setReadOnly(false); setInitialTab("detail"); setOpen(true); }}
                          className="h-8 w-8 rounded-md hover:bg-[#F3F4F6] flex items-center justify-center"
                          data-testid={`btn-edit-${r.id}`}
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4 text-[#0055FF]" />
                        </button>
                        {canDelete && (
                          <button
                            onClick={() => askDelete(r)}
                            className="h-8 w-8 rounded-md hover:bg-[#FEE2E2] flex items-center justify-center"
                            data-testid={`btn-del-${r.id}`}
                            title="Hapus tiket (admin)"
                          >
                            <Trash2 className="h-4 w-4 text-[#991B1B]" />
                          </button>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                );
              })}
              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-[#9CA3AF]">
                    {canCreate ? "Tidak ada tiket. Klik \"Tiket Baru\" untuk mulai." : "Tidak ada tiket."}
                  </td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-[#9CA3AF]">Memuat...</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile: job card list */}
        <ul className="md:hidden divide-y divide-[#E5E7EB]" data-testid="mobile-job-cards">
          {rows.length === 0 && !loading && (
            <li className="px-6 py-12 text-center text-sm text-[#9CA3AF]">
              {canCreate ? "Tidak ada tiket. Klik \"Tiket Baru\" untuk mulai." : "Tidak ada tiket."}
            </li>
          )}
          {loading && (
            <li className="px-6 py-12 text-center text-sm text-[#9CA3AF]">Memuat...</li>
          )}
          {!loading && pageRows.map((r) => {
            const c = themeForStatus(r.status, statusMaster);
            const isArchive = r.status === "DONE" || r.status === "CANCEL";
            const canClaim = ["OPEN", "RESCHEDULE"].includes(r.status) && !(r.pic && r.pic !== user?.name);
            const openView = () => { setEditing(r); setReadOnly(true); setInitialTab("detail"); setOpen(true); };
            const openEdit = (e) => { e.stopPropagation(); setEditing(r); setReadOnly(false); setInitialTab("detail"); setOpen(true); };
            const doClaim = async (e) => {
              e.stopPropagation();
              try {
                await api.post(`/tickets/${r.id}/claim`);
                toast.success(`Tiket ${r.no_tiket} diklaim`);
                load();
              } catch (err) {
                toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Gagal klaim");
              }
            };
            const doDelete = (e) => { e.stopPropagation(); askDelete(r); };
            return (
              <li
                key={r.id}
                role="button"
                tabIndex={0}
                onClick={openView}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openView(); } }}
                data-testid={`mobile-job-${r.id}`}
                className={`px-4 py-3 cursor-pointer transition-transform duration-100 active:scale-[0.985] active:bg-[#F8F9FA] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0055FF] ${selected.has(r.id) ? "bg-[#FEF9C3]" : ""}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono-tab font-semibold text-[13px] text-[#0A0A0A] truncate">
                    {r.no_tiket || "-"}
                  </span>
                  <span
                    className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border flex-shrink-0"
                    style={{ background: c.bg, color: c.fg, borderColor: c.border }}
                  >
                    {r.status}
                  </span>
                </div>
                <div className="mt-1 text-[10px] font-mono-tab text-[#9CA3AF]">
                  <div>{r.tgl_masuk || "-"}</div>
                  {r.completed_at && (
                    <div>
                      <span className="text-[#B0B6BE]">selesai:</span>{" "}
                      {formatWIBDateTime24(r.completed_at)}
                    </div>
                  )}
                </div>
                <div className="mt-1.5 text-sm text-[#0A0A0A] font-medium line-clamp-2 break-words">
                  {r.objek_lokasi || "-"}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-[#4B5563]">
                  {r.wilayah && <span>{r.wilayah}</span>}
                  {r.pic && <span className="text-[#9CA3AF]">·</span>}
                  {r.pic && <span className="truncate max-w-[55%]">{r.pic}</span>}
                  {r.prioritas && (
                    <span className="ml-auto font-bold font-mono-tab" style={{ color: PRIORITY_COLORS[r.prioritas] || "#4B5563" }}>
                      {r.prioritas}
                    </span>
                  )}
                </div>
                {r.jenis_pekerjaan && (
                  <div className="mt-1">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-[#4B5563] bg-[#F3F4F6] px-1.5 py-0.5 rounded">
                      {r.jenis_pekerjaan}
                    </span>
                  </div>
                )}
                <div className="mt-2 flex items-center gap-2">
                  {canClaim && (
                    <button
                      type="button"
                      onClick={doClaim}
                      data-testid={`mobile-claim-${r.id}`}
                      className="h-8 px-3 rounded-md bg-[#0055FF] hover:bg-[#0044CC] active:scale-[0.97] text-white text-xs font-semibold flex items-center gap-1 transition-transform duration-100"
                    >
                      <Hand className="h-3.5 w-3.5" /> Klaim
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={openEdit}
                    data-testid={`mobile-edit-${r.id}`}
                    disabled={isArchive && user?.role !== "admin" && user?.role !== "koordinator"}
                    className="h-8 px-3 rounded-md border border-[#E5E7EB] hover:bg-[#F8F9FA] active:scale-[0.97] text-xs font-semibold text-[#0055FF] flex items-center gap-1 transition-transform duration-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); openView(); }}
                    data-testid={`mobile-view-${r.id}`}
                    className="h-8 px-3 rounded-md border border-[#E5E7EB] hover:bg-[#F8F9FA] active:scale-[0.97] text-xs font-semibold text-[#4B5563] flex items-center gap-1 transition-transform duration-100"
                  >
                    <Eye className="h-3.5 w-3.5" /> Lihat
                  </button>
                  {canDelete && (
                    <button
                      type="button"
                      onClick={doDelete}
                      data-testid={`mobile-del-${r.id}`}
                      className="ml-auto h-8 w-8 rounded-md hover:bg-[#FEE2E2] active:scale-[0.97] flex items-center justify-center transition-transform duration-100"
                      title="Hapus (admin)"
                    >
                      <Trash2 className="h-4 w-4 text-[#991B1B]" />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <TicketForm
        open={open}
        onOpenChange={setOpen}
        initial={editing}
        readOnly={readOnly}
        initialTab={initialTab}
        onSaved={load}
      />

      {/* Admin-only delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent className="max-w-md" data-testid="delete-ticket-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black tracking-tighter flex items-center gap-2">
              <span className="h-9 w-9 rounded-md bg-[#FEE2E2] text-[#991B1B] flex items-center justify-center">
                <Trash2 className="h-5 w-5" />
              </span>
              Hapus Tiket?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-[#4B5563]">
            Tiket ini akan dihapus secara permanen dan tidak dapat dibatalkan.
            {deleteTarget && (
              <div className="mt-3 rounded-md border border-[#E5E7EB] bg-[#F8F9FA] p-3 text-xs">
                <div className="font-mono-tab text-[#0055FF]">{deleteTarget.no_tiket}</div>
                <div className="mt-0.5 font-semibold text-[#0A0A0A]">{deleteTarget.objek_lokasi || "-"}</div>
                <div className="text-[#9CA3AF]">{deleteTarget.wilayah || "-"} · {deleteTarget.status}</div>
              </div>
            )}
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setDeleteTarget(null)}
              className="h-10 px-4 rounded-md border border-[#E5E7EB] text-sm font-semibold text-[#4B5563] hover:bg-[#F8F9FA]"
              data-testid="delete-cancel"
            >
              Batal
            </button>
            <button
              type="button"
              onClick={confirmDelete}
              disabled={deleting}
              data-testid="delete-confirm"
              className="h-10 px-5 rounded-md bg-[#991B1B] hover:bg-[#7F1D1D] disabled:bg-[#9CA3AF] text-white font-semibold flex items-center gap-2 transition-colors"
            >
              {deleting && <Loader2 className="h-4 w-4 animate-spin" />}
              Hapus Permanen
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin-only bulk delete confirmation */}
      <Dialog open={bulkConfirm} onOpenChange={setBulkConfirm}>
        <DialogContent className="max-w-md" data-testid="bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black tracking-tighter flex items-center gap-2">
              <span className="h-9 w-9 rounded-md bg-[#FEE2E2] text-[#991B1B] flex items-center justify-center">
                <Trash2 className="h-5 w-5" />
              </span>
              Hapus {selected.size} Tiket?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-[#4B5563]">
            <b>{selected.size}</b> tiket akan dihapus secara permanen sekaligus dan tidak dapat dibatalkan.
            Digunakan untuk membersihkan tiket TEST/sampah.
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setBulkConfirm(false)}
              className="h-10 px-4 rounded-md border border-[#E5E7EB] text-sm font-semibold text-[#4B5563] hover:bg-[#F8F9FA]"
            >
              Batal
            </button>
            <button
              type="button"
              onClick={confirmBulkDelete}
              disabled={deleting}
              data-testid="bulk-delete-confirm"
              className="h-10 px-5 rounded-md bg-[#991B1B] hover:bg-[#7F1D1D] disabled:bg-[#9CA3AF] text-white font-semibold flex items-center gap-2 transition-colors"
            >
              {deleting && <Loader2 className="h-4 w-4 animate-spin" />}
              Hapus Permanen
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export date-range picker */}
      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="max-w-md" data-testid="export-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black tracking-tighter flex items-center gap-2">
              <span className="h-9 w-9 rounded-md bg-[#0055FF]/10 text-[#0055FF] flex items-center justify-center">
                <Download className="h-5 w-5" />
              </span>
              Ekspor Excel
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            {/* Feb 2026 — Preview filter aktif supaya user tahu ekspor
                akan langsung terpotong sesuai filter halaman (backend
                `/reports/tickets-export` sudah menerima semua param). */}
            {(() => {
              const chips = [];
              if (filters.q) chips.push(["Cari", filters.q]);
              if (filters.status) chips.push(["Status", filters.status]);
              if (filters.prioritas) chips.push(["Prioritas", filters.prioritas]);
              if (filters.jenis_pekerjaan) chips.push(["Jenis", filters.jenis_pekerjaan]);
              if (filters.wilayah) chips.push(["Wilayah", filters.wilayah]);
              if (filters.pic) chips.push(["PIC", filters.pic]);
              if (chips.length === 0) {
                return (
                  <div
                    className="text-xs text-[#6B7280] bg-[#F9FAFB] border border-[#E5E7EB] rounded-md px-3 py-2"
                    data-testid="export-filter-preview-empty"
                  >
                    Tidak ada filter aktif — ekspor mencakup semua tiket dalam rentang tanggal.
                  </div>
                );
              }
              return (
                <div
                  className="bg-[#EFF6FF] border border-[#BFDBFE] rounded-md px-3 py-2"
                  data-testid="export-filter-preview"
                >
                  <div className="text-[10px] font-bold uppercase tracking-widest text-[#1E40AF] mb-1.5">
                    Filter aktif akan diterapkan
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {chips.map(([k, v]) => (
                      <span
                        key={k}
                        className="inline-flex items-center gap-1 bg-white border border-[#BFDBFE] text-[#1E3A8A] text-xs font-semibold px-2 py-0.5 rounded"
                        data-testid={`export-filter-chip-${k.toLowerCase()}`}
                      >
                        <span className="text-[9px] uppercase tracking-widest text-[#3B82F6]">{k}</span>
                        <span>{v}</span>
                      </span>
                    ))}
                  </div>
                </div>
              );
            })()}
            <div className="text-[#4B5563]">
              Pilih rentang tanggal (kolom <b>Tgl Masuk</b>). Kosongkan agar semua data ikut terekspor.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Dari</label>
                <input
                  type="date"
                  data-testid="export-date-from"
                  value={exportRange.date_from}
                  onChange={(e) => setExportRange((r) => ({ ...r, date_from: e.target.value }))}
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Sampai</label>
                <input
                  type="date"
                  data-testid="export-date-to"
                  value={exportRange.date_to}
                  onChange={(e) => setExportRange((r) => ({ ...r, date_to: e.target.value }))}
                  className={inputCls}
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { label: "Bulan Ini", days: null, thisMonth: true },
                { label: "7 Hari", days: 7 },
                { label: "30 Hari", days: 30 },
                { label: "Semua", clear: true },
              ].map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => {
                    if (p.clear) return setExportRange(emptyRange());
                    if (p.thisMonth) return setExportRange(computeThisMonthRange());
                    return setExportRange(computeRollingDaysRange(p.days));
                  }}
                  className="h-8 px-3 rounded-md border border-[#E5E7EB] text-xs font-semibold text-[#4B5563] hover:bg-[#F8F9FA]"
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setExportOpen(false)}
              className="h-10 px-4 rounded-md border border-[#E5E7EB] text-sm font-semibold text-[#4B5563] hover:bg-[#F8F9FA]"
            >
              Batal
            </button>
            <button
              type="button"
              onClick={downloadMonthlyPdf}
              data-testid="export-monthly-pdf"
              className="h-10 px-4 rounded-md border-2 border-[#0055FF] text-[#0055FF] hover:bg-[#EFF6FF] text-sm font-bold flex items-center gap-2 transition-colors"
            >
              <Download className="h-4 w-4" />
              PDF Rekap Bulanan
            </button>
            <button
              type="button"
              onClick={() => onExport(exportRange)}
              data-testid="export-download"
              className="h-10 px-5 rounded-md bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold flex items-center gap-2 transition-colors"
            >
              <Download className="h-4 w-4" />
              Unduh Excel
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </PullToRefresh>
  );
}
